/**
 * BookingLayout — minimal chrome for the public booking page.
 *
 * The header carries only the Ahoy logo; identity (host, event title)
 * lives inside the main content via BookingHostCard so the chrome stays
 * consistent across single-host, round_robin, and collective bookings.
 *
 * Content sits in a centred white card on a tinted background to mirror
 * the visual treatment of dedicated booking products (Cal.com / Calendly).
 */

import type { ReactNode } from 'react';

import { useBookingEmbedMessaging } from '@/lib/scheduling/bookingEmbed';

interface BookingLayoutProps {
  /** Content to render in the main area */
  children: ReactNode;
  /** Render without the outer app chrome for iframe embedding */
  embed?: boolean;
}

export function BookingLayout({ children, embed = false }: BookingLayoutProps) {
  const cardRef = useBookingEmbedMessaging(embed);

  if (embed) {
    return (
      <div ref={cardRef} className='bg-[var(--color-bg-primary)]'>
        <div className='p-4 sm:p-6 md:p-8'>{children}</div>
        <div className='pb-4 text-center'>
          <a
            href='https://ahoy.ai'
            target='_blank'
            rel='noopener noreferrer'
            className='text-xs text-[var(--color-text-tertiary)] hover:underline'
            aria-label='Powered by Ahoy'
          >
            Powered by Ahoy
          </a>
        </div>
      </div>
    );
  }

  return (
    // Full-viewport flex column. With the top-left logo gone, the page
    // needs to feel anchored — `justify-center` puts the content card in
    // the optical centre when content is short, and naturally falls back
    // to a top-aligned scroll when content is taller than the viewport.
    //
    // `min-h-dvh` (dynamic viewport height) over `min-h-screen` so the
    // wrapper tracks the visible viewport on mobile — Safari/Chrome shrink
    // the address bar on scroll, and `100vh` includes that retracted chrome,
    // causing a visible jump as the bar collapses. `dvh` follows the live
    // viewport size and avoids that reflow.
    <div className='flex min-h-dvh flex-col bg-[var(--color-bg-secondary)]'>
      {/* Main content area — uses flex itself so the card sits centred
          within the available vertical space between (now-empty) top
          and footer. Padding scales with viewport: phones get a tight
          gutter (px-3 py-6); tablets+ get roomier breathing space. */}
      <main className='flex w-full flex-1 items-center justify-center px-3 py-6 sm:px-6 sm:py-10'>
        <div className='w-full max-w-5xl rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-sm sm:rounded-2xl'>
          <div className='p-4 sm:p-6 md:p-8'>{children}</div>
        </div>
      </main>

      {/* Footer — "Powered by" caption above the pennant mark. The
          pennant itself contains the "AHOY" wordmark; spelling Ahoy out
          in the caption duplicated it. Caption now reads as a lead-in
          to the brand mark rather than redundant text. */}
      <footer className='px-4 py-6 sm:px-6 sm:py-8'>
        <div className='flex flex-col items-center gap-1.5'>
          <p className='text-xs text-[var(--color-text-tertiary)]'>
            Powered by
          </p>
          <a
            href='https://ahoy.ai'
            className='inline-flex'
            aria-label='Ahoy home'
          >
            <img
              src='/ahoy-pennant.png'
              alt='Ahoy'
              className='h-10 w-auto sm:h-12'
            />
          </a>
        </div>
      </footer>
    </div>
  );
}
